import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { UserPlus, Search, Download } from 'lucide-react';

const HowItWorksSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const steps = [
    {
      number: '01',
      icon: UserPlus,
      title: 'Kostenlos registrieren',
      description: 'Erstellen Sie in wenigen Sekunden Ihr kostenloses Konto und erhalten Sie sofort 10 Credits geschenkt. Keine Kreditkarte erforderlich.'
    },
    {
      number: '02',
      icon: Search,
      title: 'Leads filtern & finden',
      description: 'Wählen Sie Ihr Wunsch-Bundesland, Ihre Zielstädte und die passende Branche. Unser intelligenter Filter findet Ihre perfekten B2B-Kontakte.'
    },
    {
      number: '03',
      icon: Download,
      title: 'Herunterladen & loslegen',
      description: 'Exportieren Sie Ihre Kontakte als CSV-Datei mit nur einem Klick. Importieren Sie sie in Ihr CRM und starten Sie Ihre Kampagne.'
    }
  ];

  return (
    <section ref={ref} className="py-24 bg-gradient-to-br from-[#2835F7] to-[#1a25c9] relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            So einfach geht's
          </h2>
          <p className="text-lg text-white/90 max-w-3xl mx-auto">
            In nur 3 Schritten zu Ihren perfekten B2B-Leads. 
            Schnell, unkompliziert und ohne versteckte Kosten.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 relative">
          {/* Connection lines (desktop only) */}
          <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-white/20 -translate-y-1/2" />

          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.2 }}
              className="relative"
            >
              {/* Step number circle */}
              <div className="flex justify-center mb-6">
                <div className="relative z-10 w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-2xl">
                  <span className="text-3xl font-bold text-[#2835F7]">{step.number}</span>
                </div>
              </div>

              {/* Content card */}
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center hover:bg-white/20 transition-all duration-300 border border-white/20 shadow-xl">
                <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <step.icon className="w-8 h-8 text-[#2835F7]" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{step.title}</h3>
                <p className="text-white/90 leading-relaxed">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-16 text-center"
        >
          <p className="text-xl text-white/90 font-semibold">
            Von der Registrierung bis zum ersten Export: <span className="text-white">unter 2 Minuten!</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorksSection;